import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import { useLanguage } from "../context/LanguageContext";
import { t3, pick3 } from "../i18n/v3";
import { PageShell, PageHeader, EmptyState } from "../components/PageShell";
import PageBackdrop from "../components/PageBackdrop";
import useJsonLd from "../hooks/useJsonLd";
import { API, pickContent, sortContent } from "../lib/content";

const SITE = "https://lifeisaproject.global";
const ease = [0.16, 1, 0.3, 1];

/**
 * ResearchPage — sourced from the CMS (published research only). The
 * legacy research essays live here as published CMS records; drafts
 * never appear. Every item shows its content language.
 */
export default function ResearchPage() {
    const { lang } = useLanguage();
    const [items, setItems] = useState([]);

    useEffect(() => {
        axios.get(`${API}/content/research`).then((r) => setItems(r.data || [])).catch(() => {});
    }, []);

    const sorted = sortContent(items);
    const cats = pick3(t3.research.categories, lang);

    useJsonLd("itemlist-research", sorted.length ? {
        "@context": "https://schema.org",
        "@type": "ItemList",
        name: "Research — Life is a Project",
        url: `${SITE}/research`,
        numberOfItems: sorted.length,
        itemListElement: sorted.map((e, i) => ({
            "@type": "ListItem",
            position: i + 1,
            url: `${SITE}/research/${e.slug}`,
            name: e.title,
        })),
    } : null);

    return (
        <PageShell testId="page-research" backdrop={<PageBackdrop variant="research" />}>
            <PageHeader
                overline={pick3(t3.research.overline, lang)}
                title={pick3(t3.research.title, lang)}
                kicker={pick3(t3.research.lede, lang)}
            />

            <section aria-labelledby="research-cats" className="mb-16">
                <h2 id="research-cats" className="font-mono text-[11px] tracking-[0.28em] uppercase text-[color:var(--warm-gray-2)] mb-5">
                    {pick3(t3.research.categoriesHeading, lang)}
                </h2>
                <ul data-testid="research-categories" className="grid grid-cols-2 md:grid-cols-3 border-t border-[color:var(--line-strong)]">
                    {cats.map((c, i) => (
                        <li
                            key={c}
                            className={`border-b border-[color:var(--line)] py-5 md:py-6 ${i % 3 !== 0 ? "md:border-l md:pl-6" : ""} ${i % 2 !== 0 ? "border-l pl-4 md:pl-6" : ""}`}
                        >
                            <span className="font-display text-[18px] md:text-[22px] tracking-[-0.005em] text-[color:var(--navy)]">{c}</span>
                        </li>
                    ))}
                </ul>
            </section>

            {sorted.length === 0 ? (
                <EmptyState label={pick3(t3.research.empty, lang)} />
            ) : (
                <ul data-testid="research-list" className="border-t border-[color:var(--line-strong)]">
                    {sorted.map((e, i) => {
                        const { title, subtitle: dek } = pickContent(e, lang);
                        return (
                            <motion.li
                                key={e.slug}
                                initial={{ opacity: 0, y: 12 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true, margin: "-8%" }}
                                transition={{ duration: 0.7, ease, delay: i * 0.05 }}
                                className="border-b border-[color:var(--line)]"
                            >
                                <Link
                                    to={`/research/${e.slug}`}
                                    data-testid="research-card"
                                    className="grid grid-cols-12 gap-6 py-9 items-baseline group"
                                >
                                    <div className="col-span-3 md:col-span-2 font-mono text-[11px] tracking-[0.24em] uppercase text-[color:var(--warm-gray-2)]">
                                        {e.number || ""}
                                    </div>
                                    <div className="col-span-9 md:col-span-7">
                                        {e.category && (
                                            <div className="font-mono text-[10px] tracking-[0.28em] uppercase text-[color:var(--horizon)] mb-2">{e.category}</div>
                                        )}
                                        <h3 className="font-display text-[26px] md:text-[36px] leading-[1.08] tracking-[-0.01em] text-[color:var(--navy)] group-hover:italic transition-all duration-500">
                                            {title}
                                        </h3>
                                        {dek && (
                                            <p className="mt-3 max-w-[60ch] text-[15px] md:text-[16px] leading-[1.6] text-[color:var(--navy)]/75">
                                                {dek}
                                            </p>
                                        )}
                                    </div>
                                    <div className="col-span-12 md:col-span-3 md:text-right font-mono text-[11px] tracking-[0.24em] uppercase text-[color:var(--warm-gray-2)] flex items-center md:justify-end gap-3">
                                        {e.reading_time && <span>{e.reading_time}</span>}
                                        <span data-testid="content-lang-badge" className="text-[10px] text-[color:var(--navy)]/50 border border-[color:var(--line)] px-1.5 py-0.5">
                                            {(e.language || "en").toUpperCase()}
                                        </span>
                                        <span aria-hidden className="w-4 h-px bg-[color:var(--gold)] transition-all duration-500 group-hover:w-8" />
                                    </div>
                                </Link>
                            </motion.li>
                        );
                    })}
                </ul>
            )}
        </PageShell>
    );
}
